import React, {
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useState,
} from "react";
import { useAuthStore } from "@/features/auth";
import { Message, useMessagesStore } from "@/entities/message";
import { ChatMessageWrapper, ChatMessageItem } from "./message-item";
import {
  useMessageScroll,
  useMessageSelection,
  useMessageReadObserver,
  useMessageSelectionStore,
} from "../../../model";
import { Spinner } from "react-bootstrap";
import { MessageContextMenu } from "../message-context-menu";
import "../chat-messages.css";
import { ChatMessageListProps } from "./chat-message-list.props";
import { useShallow } from "zustand/react/shallow";
import { FileItem, FileType } from "@/shared/lib";
import { DateSeparator } from "./date-separator";
import { formatDateForSeparator } from "@/features/chat/lib";
import { useFloatingDate } from "@/features/chat/lib/use-floating-date";

export const ChatMessageList = forwardRef<
  { scrollToBottom: () => void },
  ChatMessageListProps
>(
  (
    {
      chatId,
      currentChat,
      chatContainerRef,
      registeredContact,
      onReply,
      onDoubleClick,
      onDeleteMessage,
      onEditMessage,
      onResend,
      setShowSelectChatModal,
      forwardSelectedMessages,
    },
    ref
  ) => {
    const { user } = useAuthStore();
    const messages = useMessagesStore(useShallow((state) => state.messages[chatId]))

    const totalCount = useMessagesStore((state) => state.totalCount[chatId]);
    const allFiles = useMessagesStore((state) => state.files[chatId]);
    const loadMessages = useMessagesStore((state) => state.loadMessages);
    const page = useMessagesStore((state) => state.page[chatId]);
    const pages = useMessagesStore((state) => state.pages[chatId]);
    const activeMsg = useMessagesStore((state) => state.activeMsg[chatId]);
    const setActiveMsg = useMessagesStore((state) => state.setActiveMsg);
    const loadingMessage = useMessagesStore((state) => state.loadingMessage);
    const loadingChatMessages = useMessagesStore(
      (state) => state.loadingChatMessages[chatId]
    );
    
    const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const messageItemRef = useRef<HTMLDivElement>(null);

    const {
      hasScrolled,
      highlightedMessageId,
      isRapidClicking,
      scrollToMessage,
      firstUnreadIndex,
      // unreadCount,
      // userHasScrolledManually,
      scrollToMessageRef,
    } = useMessageScroll({
      chatId: chatId,
      messages,
      chatContainerRef,
      user,
    });

    const modeSelected = useMessageSelectionStore(
      (state) => state.modeSelected
    );
    const selectedMessages = useMessageSelectionStore(
      (state) => state.selectedMessages
    );

    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    const {
      handleMouseDown,
      handleMouseEnter,
      handleMouseUp,
      copySelectedMessages,
      resetSelection,
      deleteSelectedMessages,
    } = useMessageSelection({
      messagesRef,
      user,
      onDeleteMessages: async (ids) => {
        if (!chatId) return;
        resetSelection();

        onDeleteMessage(ids);
      },
      onCopySuccess: () => {
        console.log("Сообщения скопированы");
      },
    });

    useMessageReadObserver({
      chatId: chatId,
      messages,
      user,
      messageRefs,
      chatContainerRef,
      hasScrolled,
    });

    const isFirstLoad = useRef(true);

    // первая загрузка чата
    useEffect(() => {
      if (!currentChat || !messages) return;

      const el = chatContainerRef.current;
      const chatId = currentChat._id;

      if (!messages.length) {
        loadMessages(chatId, 1).then(() => {
          requestAnimationFrame(() => {
            if (el) el.scrollTop = el.scrollHeight;
          });
          isFirstLoad.current = false;
        });
      }
    }, [currentChat]);

    const [loadingMore, setLoadingMore] = useState(false);

    const loadMoreIfAtTop = useCallback(async () => {
      const el = chatContainerRef.current;
      if (!el || !currentChat || !messages?.length) return;

      const firstMessage = messages.find(
        (msg) => msg._id === currentChat.firstMessage?._id
      );
      if (firstMessage) return;

      const total = totalCount ?? Infinity;
      if (messages.length >= total || loadingChatMessages || loadingMore)
        return;

      if (el.scrollTop < 600) {
        setLoadingMore(true);
        try {
          const prevScrollHeight = el.scrollHeight;
          const prevScrollTop = el.scrollTop;
          const currentPage = Math.max(...pages);

          await loadMessages(currentChat._id, currentPage + 1);

          const newScrollHeight = el.scrollHeight;
          el.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
        } finally {
          setLoadingMore(false);
        }
      }
    }, [
      currentChat,
      messages,
      pages,
      totalCount,
      loadingChatMessages,
      loadMessages,
      loadingMore,
    ]);


    const loadingMoreBottomRef = useRef(false);
    const scrollTickRef = useRef<number | null>(null);

    const loadMoreIfAtBottom = useCallback(async () => {
      const el = chatContainerRef.current;
      if (!el || !currentChat || !messages?.length) return;

      if (scrollToMessageRef.current) {
        return;
      }
      const lastMessage = messages.find(
        (msg) => msg._id === currentChat.lastMessage?._id
      );
      if (lastMessage) return;

      if (loadingChatMessages || loadingMoreBottomRef.current) return;

      const scrollBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (scrollBottom < 600) {
        loadingMoreBottomRef.current = true;
        try {
          const prevScrollTop = el.scrollTop;

          const currentPage = Math.min(...pages);
          if (currentPage > 1) {
            await loadMessages(currentChat._id, currentPage - 1);
          }

          await new Promise((resolve) => requestAnimationFrame(resolve));

          el.scrollTop = prevScrollTop;
        } finally {
          loadingMoreBottomRef.current = false;
        }
      }
    }, [
      currentChat,
      messages,
      pages,
      totalCount,
      loadingChatMessages,
      loadMessages,
      scrollToMessageRef,
    ]);


    const {
      floatingDate,
      isFading,
      updateFloatingDate,
      formattedFloatingDate,
    } = useFloatingDate({
      messages,
      messageRefs,
      chatContainerRef,
      enabled: !!messages?.length,
    });

    // handleScroll — дебаунсим через requestAnimationFrame, чтобы не дергать логику десятки раз за кадр
    const handleScroll = useCallback(() => {
      if (!currentChat) return;
      if (scrollTickRef.current) cancelAnimationFrame(scrollTickRef.current);
      scrollTickRef.current = requestAnimationFrame(() => {
        loadMoreIfAtTop();
        loadMoreIfAtBottom();
        updateFloatingDate();
        scrollTickRef.current = null;
      });
    }, [currentChat, loadMoreIfAtTop, loadMoreIfAtBottom, updateFloatingDate]);

    useEffect(() => {
      const container = chatContainerRef.current;
      if (!container) return;
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }, [handleScroll]);

    const [show, setShow] = useState(false);
    const [target, setTarget] = useState<HTMLElement | null>(null);
    const contextMenuRef = useRef<HTMLDivElement>(null);

    const handleRightClick = (
      e: React.MouseEvent<HTMLDivElement>,
      msg: Message,
      type?: FileType,
      file?: FileItem
    ) => {
      e.preventDefault();
      e.stopPropagation()
      setShow(true);
      setTarget(e.currentTarget);
      setActiveMsg(chatId, { msg, type, file });
    };

    const [visible, setVisible] = useState(false);

    useEffect(() => {
      const timer = setTimeout(() => {
        setVisible(true);
      }, 500);

      return () => clearTimeout(timer);
    }, []);

    return (
      <>
        {/* {messages && messages.length > 0 && !hasScrolled && !loadingChatMessages && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
            style={{
              backgroundColor: "rgba(255,255,255,0.5)",
              pointerEvents: "none",
            }}
          >
            <Spinner animation="border" variant="secondary" />
          </div>
        )} */}

        <div
          ref={chatContainerRef}
          className="scrollbar-custom-y flex-grow-1 d-flex flex-column"
          style={{
            overflowY: "auto",
            // opacity: 1,
            opacity: visible ? 1 : 0,
            // pointerEvents: "auto",
            pointerEvents: visible ? "auto" : "none",
            height: "100%",
            overflowX: "hidden",
          }}
        >
          {floatingDate && (
            <div
              className={`position-absolute start-0 text-center py-1 transition-opacity ${isFading ? "opacity-0" : "opacity-100"
                }`}
              style={{
                zIndex: 10,
                top: -5,
                width: "calc(100% - 4px)",
                transition: "opacity 300ms ease-in-out",
              }}
            >
              <span
                className="bg-secondary text-white px-3 py-1 rounded-pill"
                style={{ fontSize: "12px", fontWeight: 500 }}
              >
                {formatDateForSeparator(floatingDate)}
              </span>
            </div>
          )}

          <div style={{ marginTop: "auto" }} />

          {loadingMore && (
            <div className="w-100 p-3 d-flex justify-content-center">
              <Spinner animation="border" size="sm" variant="secondary" />
            </div>
          )}

          {messages?.map((msg, idx) => {
            const isUserMsg = msg.owner._id === user?._id;
            const isFirstUnread =
              firstUnreadIndex !== null && idx === firstUnreadIndex;

            const msgDate = new Date(msg.createdAt);

            const shouldShowDate =
              idx === 0 ||
              !messages[idx - 1] ||
              Math.abs(
                new Date(
                  messages[idx - 1].createdAt || messages[idx - 1].createdAt
                ).getTime() - msgDate.getTime()
              ) >
              24 * 60 * 60 * 1000;

            return (
              <React.Fragment key={`fragment-${msg._id}-${idx}`}>
                {shouldShowDate && <DateSeparator date={msgDate} />}

                <ChatMessageWrapper
                  key={`${msg._id}-${idx}`}
                  idx={idx}
                  msg={msg}
                  isUserMsg={isUserMsg}
                  messageRefs={messageRefs}
                  highlightedMessageId={highlightedMessageId}
                  isRapidClicking={isRapidClicking}
                  isFirstUnread={isFirstUnread}
                  onReply={() => onReply(msg)}
                  modeSelected={modeSelected}
                  onMouseDown={(e) => handleMouseDown(idx, e)}
                  onMouseEnter={(e) => handleMouseEnter(idx, e)}
                  onMouseUp={() => handleMouseUp(idx)}
                  onSelectMessage={() => {
                    if (modeSelected && !loadingMessage[msg._id]) {
                      // toggleSelectMessage(msg._id);
                    }
                  }}
                >
                  <ChatMessageItem
                    key={`${msg._id}-${idx}-item`}
                    allFiles={allFiles}
                    msg={msg}
                    handleRightClick={handleRightClick}
                    isUserMsg={isUserMsg}
                    loadingMessage={loadingMessage[msg._id] || false}
                    onDoubleClick={() => onDoubleClick(msg)}
                    onClickMessegeReply={(msgId) => scrollToMessage(chatId, msgId)}
                    goToMessage={(msgId) => scrollToMessage(chatId, msgId)}
                    onEditMessage={onEditMessage}
                    onDeleteMessage={onDeleteMessage}
                    modeSelected={modeSelected}
                    isSelected={selectedMessages.has(msg._id)}
                    ref={messageItemRef}
                    onResend={() => onResend(msg)}
                  />
                </ChatMessageWrapper>
              </React.Fragment>
            );
          })}
        </div>

        {activeMsg && (
          <MessageContextMenu
            show={show}
            target={target}
            isUser={user?._id === activeMsg?.msg.owner._id}
            chatId={chatId}
            onHide={() => setShow(false)}
            activeMsg={activeMsg}
            modeSelected={modeSelected}
            onSelectMessage={(id) => {
              useMessageSelectionStore.getState().toggleMessage(id);
            }}
            onCopySelected={copySelectedMessages}
            onDeleteSelected={() => {
              deleteSelectedMessages();
              useMessageSelectionStore.getState().exitSelectionMode();
            }}
            onForwardSelected={forwardSelectedMessages}
            onDeleteMessage={onDeleteMessage}
            onCancelSelection={resetSelection}
            setShowSelectChatModal={setShowSelectChatModal}
          />
        )}
      </>
    );
  }
);
